import React from 'react';

/**
 * Material UI
 */
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Container, Grid, Button, Paper, Chip } from '@material-ui/core';
import { 
	School as SchoolIcon, 
	TrendingUp as TrendingUpIcon, 
	Share as ShareIcon, 
	AccessTime as AccessTimeIcon,
	CheckCircle as CheckCircleIcon,
	Star as StarIcon
} from '@material-ui/icons';

/**
 * Application
 */
import { SigninSection, ThumbnailSection, SideBySideSection, SubscriptionSection } from '../../common/components/Sections';
import { INTRO_BLOCKS } from '../Constants';
import { GoogleLoginButton } from '../../common/components/controls';

export function Home(props) {
	const { t, account } = props;

	const classes = makeStyles(theme => ({
		root: {
			width: "100%",
			color: "#1a1a1a",
			fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
			textAlign: "center",
			backgroundColor: "#ffffff"
		},
		heroSection: {
			background: 'linear-gradient(135deg, #fdba74 0%, #fb923c 100%)',
			color: 'white',
			padding: theme.spacing(12, 0),
			position: 'relative',
			overflow: 'hidden'
		},
		heroBackground: {
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			opacity: 0.1,
			background: 'url(/images/parents-and-kids-transparent.png) center/cover',
			zIndex: 0
		},
		heroContent: {
			position: 'relative',
			zIndex: 1
		},
		heroTitle: {
			fontSize: '3.5rem',
			fontWeight: 700,
			marginBottom: theme.spacing(2),
			lineHeight: 1.2
		},
		heroSubtitle: {
			fontSize: '1.25rem',
			fontWeight: 400,
			marginBottom: theme.spacing(4),
			opacity: 0.9
		},
		ctaButton: {
			background: 'white',
			color: '#ea580c',
			padding: theme.spacing(2, 4),
			fontSize: '1.1rem',
			fontWeight: 600,
			borderRadius: '50px',
			textTransform: 'none',
			boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
			'&:hover': {
				background: '#f8f9fa',
				transform: 'translateY(-2px)',
				boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
			}
		},
		featuresSection: {
			padding: theme.spacing(10, 0),
			backgroundColor: '#f8f9fa'
		},
		sectionTitle: {
			fontSize: '2.5rem',
			fontWeight: 700,
			marginBottom: theme.spacing(1),
			color: '#1a1a1a'
		},
		sectionSubtitle: {
			fontSize: '1.1rem',
			color: '#6b7280',
			marginBottom: theme.spacing(6),
			maxWidth: '600px',
			margin: '0 auto 3rem'
		},
		featureCard: {
			background: 'white',
			borderRadius: '16px',
			padding: theme.spacing(4),
			height: '100%',
			boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
			transition: 'all 0.3s ease',
			border: '1px solid #e5e7eb',
			'&:hover': {
				transform: 'translateY(-4px)',
				boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1)'
			}
		},
		featureIcon: {
			width: '60px',
			height: '60px',
			borderRadius: '16px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			marginBottom: theme.spacing(3),
			fontSize: '2rem',
			color: 'white'
		},
		featureTitle: {
			fontSize: '1.25rem',
			fontWeight: 600,
			marginBottom: theme.spacing(2),
			color: '#1a1a1a'
		},
		featureText: {
			color: '#6b7280',
			lineHeight: 1.6
		},
		statsSection: {
			padding: theme.spacing(8, 0),
			backgroundColor: '#1a1a1a',
			color: 'white'
		},
		statItem: {
			textAlign: 'center'
		},
		statNumber: {
			fontSize: '3rem',
			fontWeight: 700,
			color: '#fdba74',
			marginBottom: theme.spacing(1)
		},
		statLabel: {
			fontSize: '1.1rem',
			color: '#9ca3af'
		},
		testimonialSection: {
			padding: theme.spacing(10, 0),
			backgroundColor: 'white'
		},
		testimonialCard: {
			background: '#f8f9fa',
			borderRadius: '16px',
			padding: theme.spacing(4),
			textAlign: 'left',
			height: '100%'
		},
		testimonialText: {
			fontSize: '1.1rem',
			fontStyle: 'italic',
			marginBottom: theme.spacing(3),
			color: '#374151',
			lineHeight: 1.6
		},
		testimonialAuthor: {
			fontWeight: 600,
			color: '#1a1a1a'
		},
		ctaSection: {
			padding: theme.spacing(10, 0),
			background: 'linear-gradient(135deg, #fdba74 0%, #fb923c 100%)',
			color: 'white'
		},
		ctaTitle: {
			fontSize: '2.5rem',
			fontWeight: 700,
			marginBottom: theme.spacing(2)
		},
		ctaSubtitle: {
			fontSize: '1.1rem',
			marginBottom: theme.spacing(4),
			opacity: 0.9
		},
		ctaButtonSecondary: {
			background: 'white',
			color: '#ea580c',
			padding: theme.spacing(2, 4),
			fontSize: '1.1rem',
			fontWeight: 600,
			borderRadius: '50px',
			textTransform: 'none',
			marginLeft: theme.spacing(2),
			'&:hover': {
				background: '#f8f9fa'
			}
		},
		badge: {
			background: 'rgba(255,255,255,0.2)',
			color: 'white',
			padding: theme.spacing(0.5, 2),
			borderRadius: '20px',
			fontSize: '0.875rem',
			fontWeight: 500,
			marginBottom: theme.spacing(2),
			display: 'inline-block'
		}
	}))();

	const features = [
		{
			icon: <AccessTimeIcon />,
			title: "Save Time",
			text: "Create test papers in minutes, not hours. Our intelligent system helps you focus on what matters most.",
			color: "#fdba74"
		},
		{
			icon: <TrendingUpIcon />,
			title: "Track Progress",
			text: "Monitor your child's learning journey with detailed analytics and performance insights.",
			color: "#10b981"
		},
		{
			icon: <ShareIcon />,
			title: "Share & Collaborate",
			text: "Connect with other parents and educators. Share resources and learn from the community.",
			color: "#3b82f6"
		},
		{
			icon: <SchoolIcon />,
			title: "Quality Education",
			text: "Access curated content aligned with Hong Kong's curriculum standards and best practices.",
			color: "#8b5cf6"
		}
	];

	const testimonials = [
		{
			text: "Test Paper Online has transformed how I prepare materials for my children. The time I save is incredible!",
			author: "Sarah Chen, Parent of P3 & P5 students"
		},
		{
			text: "Finally, a platform that understands the unique challenges of Hong Kong parents. Highly recommended!",
			author: "Michael Wong, Educator"
		},
		{
			text: "The progress tracking feature helps me understand exactly where my child needs support. Game changer!",
			author: "Lisa Lau, Parent of P2 student"
		}
	];

	return (
		<Box className={classes.root}>
			{/* Hero Section */}
			<Box className={classes.heroSection}>
				<Box className={classes.heroBackground} />
				<Container maxWidth="lg" className={classes.heroContent}>
					<Chip label="Pilot Program" className={classes.badge} />
					<Typography className={classes.heroTitle}>
						Empower Your Child's Education
					</Typography>
					<Typography className={classes.heroSubtitle}>
						Create professional test papers in minutes. Track progress. Share resources. 
						Join thousands of Hong Kong parents making education easier.
					</Typography>
					{!Boolean(account) ? (
						<Box>
							<GoogleLoginButton />
						</Box>
					) : (
						<Button className={classes.ctaButton} variant="contained">
							Get Started Today
						</Button>
					)}
				</Container>
			</Box>

			{/* Features Section */}
			<Box className={classes.featuresSection}>
				<Container maxWidth="lg">
					<Typography className={classes.sectionTitle}>
						Why Choose Test Paper Online?
					</Typography>
					<Typography className={classes.sectionSubtitle}>
						Designed specifically for Hong Kong parents and educators, 
						our platform makes creating quality educational materials effortless.
					</Typography>
					<Grid container spacing={4}>
						{features.map((feature, index) => (
							<Grid item xs={12} sm={6} md={3} key={index}>
								<Paper className={classes.featureCard} elevation={0}>
									<Box 
										className={classes.featureIcon}
										style={{ backgroundColor: feature.color }}
									>
										{feature.icon}
									</Box>
									<Typography className={classes.featureTitle}>
										{feature.title}
									</Typography>
									<Typography className={classes.featureText}>
										{feature.text}
									</Typography>
								</Paper>
							</Grid>
						))}
					</Grid>
				</Container>
			</Box>

			{/* Stats Section */}
			<Box className={classes.statsSection}>
				<Container maxWidth="lg">
					<Grid container spacing={4}>
						<Grid item xs={12} sm={4} className={classes.statItem}>
							<Typography className={classes.statNumber}>500+</Typography>
							<Typography className={classes.statLabel}>Active Parents</Typography>
						</Grid>
						<Grid item xs={12} sm={4} className={classes.statItem}>
							<Typography className={classes.statNumber}>2,000+</Typography>
							<Typography className={classes.statLabel}>Test Papers Created</Typography>
						</Grid>
						<Grid item xs={12} sm={4} className={classes.statItem}>
							<Typography className={classes.statNumber}>95%</Typography>
							<Typography className={classes.statLabel}>Time Saved</Typography>
						</Grid>
					</Grid>
				</Container>
			</Box>

			{/* Testimonials Section */}
			<Box className={classes.testimonialSection}>
				<Container maxWidth="lg">
					<Typography className={classes.sectionTitle}>
						What Parents Say
					</Typography>
					<Typography className={classes.sectionSubtitle}>
						Join thousands of satisfied parents who have transformed their approach to education.
					</Typography>
					<Grid container spacing={4}>
						{testimonials.map((testimonial, index) => (
							<Grid item xs={12} md={4} key={index}>
								<Paper className={classes.testimonialCard} elevation={0}>
									<Box display="flex" mb={2}>
										{[...Array(5)].map((_, i) => (
											<StarIcon key={i} style={{ color: '#fdba74', fontSize: '1.2rem' }} />
										))}
									</Box>
									<Typography className={classes.testimonialText}>
										"{testimonial.text}"
									</Typography>
									<Typography className={classes.testimonialAuthor}>
										— {testimonial.author}
									</Typography>
								</Paper>
							</Grid>
						))}
					</Grid>
				</Container>
			</Box>

			{/* CTA Section */}
			<Box className={classes.ctaSection}>
				<Container maxWidth="lg">
					<Typography className={classes.ctaTitle}>
						Ready to Transform Education?
					</Typography>
					<Typography className={classes.ctaSubtitle}>
						Join the community of parents making education easier and more effective.
					</Typography>
					<Box>
						{!Boolean(account) ? (
							<GoogleLoginButton />
						) : (
							<Button className={classes.ctaButtonSecondary} variant="contained">
								Start Creating
							</Button>
						)}
					</Box>
				</Container>
			</Box>

			{/* Footer */}
			<Box py={3} textAlign="center" color="#6b7280">
				<Typography variant="body2">
					Version: {process.env.REACT_APP_BUILD_TIMESTAMP}
				</Typography>
			</Box>
		</Box>
	);
}